import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { withForbidden } from '../../../../../global/components/HOCs/with-forbidden';
import { withLayout } from '../../../../../global/components/HOCs/with-layout';
import { withScrollTop } from '../../../../../global/components/HOCs/with-scroll-Top';
import { GpsType, LayoutModels } from '../../../../../global/components/layout/layout-models';
import { TcsmFeatures } from '../../../../../global/models/features-model';
import AccountInfoContainer from '../../../../account-info/components/account-info-container';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { DependentInfo } from '../../../../account-info/features/dependents/dependent-info';
import { MaritalStatusApp } from '../../../../account-info/features/marital-status/marital-status';
import { PersonalInformationApp } from '../../../../account-info/features/personal-info/personal-info';
import { MyRelationshipsApp } from '../../../../account-info/features/relationships/my-relationships';
import { useAccountInfoSettings } from '../../../../account-info/hooks/useAccountInfoSettings';
import { useDemographicsPerson } from '../../../../account-info/hooks/useDemographicsPerson';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { setCurrentNewHireBenefitEnrollmentStep, setCurrentNewHireEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { NewHireEnrollmentStep } from '../models/new-hire-enrollment-models';
import { useAnnualEnrollmentSettings } from '../../../../annual-enrollment/hooks/useAnnualEnrollmentSettings';
import { TcsmAppConfigs } from '../../../../../global/models/configuration-models';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { NewHireMemberEnrollmentPersonalInfoWizard } from './wizards/new-hire-member-enrollment-wizard';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { MemberEmploymentCompensation } from './member-employment-compensation';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import GenericStepper from '../../../../../global/components/navigation/generic-stepper';
import { getEmployeeRecordStepItems, getEmployeeRecordSteps } from './helpers/member-employee-record-helper';
import { NewHireProps } from '../../../models/arc-epi-props';
import { useBenefitEnrollmentStep } from '../hooks/useBenefitEnrollmentStep';
import { isNepEnrollment } from '../hooks/isNepEnrollment';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';

const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.MEMBER_NEW_HIRE_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.MEMBER_NEW_HIRE_PERSON_INFO)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.NEW_HIRE_MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(LayoutModels.FULL_WIDTH, GpsType.NEW_HIRE_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));
const EmploymentWrapper = withLayout(null, GpsType.MEMBER_NEW_HIRE_EMPLOYMENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));

interface Props {
	settings: TcsmAppConfigs;
}

export function MemberEmployeeRecord(props: Props) {

	const { settings } = props;
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const demographicsSettingsLoaded = useAccountInfoSettings(settings.personalInfoSettingId, settings.relationshipsSettingId);
	const aeSettingsLoaded = useAnnualEnrollmentSettings(settings.annualEnrollmentSettingsId);
	const personLoaded = useDemographicsPerson(app);
	const dispatch = useDispatch();
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const history = useHistory();
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
	const isNep = isNepEnrollment();
	const coveragePath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}${NewHireEnrollmentRoutes.BenefitEnrollment}${NewHireEnrollmentRoutes.Coverage}`);
	useEffect(() => {
		dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.EMPLOYEE_RECORD));
	}, []);

	const newHireEnrollmentRoot = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}`);

	if (!demographicsSettingsLoaded || !personLoaded || !aeSettingsLoaded) {
		return <LoadingCircle />;
	}

	const wizard = new NewHireMemberEnrollmentPersonalInfoWizard(epiProps, AnnualEnrollmentKey.newHireEnrollment, app, dispatch, history);

	return (
		<>
			<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.newHireEnrollment}>
				<GenericStepper
					basePath={routeMatch.path}
					allSteps={getEmployeeRecordSteps(isNep)}
					useCurrentStep={useBenefitEnrollmentStep}
					setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
					progressItems={getEmployeeRecordStepItems(newHireProps, isNep)}
				/>
				<Switch>
					<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
						<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<PersonalInformationApp basePath={routeMatch.path} customPersonalInfoWizard={wizard} />
						</PersonalInfoWrapper>
					)} />
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
						<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MyRelationshipsApp basePath={routeMatch.path} isMember customNextStep={isNep ? coveragePath : null} />
						</RelationshipsWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
						<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MaritalStatusApp basePath={routeMatch.path} />
						</MaritalStatusWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
						<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
						</DependentsWrapper>
					)} />
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.EmploymentSegment}`} render={() => (
						<EmploymentWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MemberEmploymentCompensation basePath={routeMatch.path} />
						</EmploymentWrapper>
					)} />
					<Route path={`${routeMatch.path}/*`}>
						<Redirect to={newHireEnrollmentRoot} />
					</Route>
				</Switch>
			</AnnualEnrollmentContext.Provider >
		</>
	);
}